import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { errorReporter, isFirebaseOfflineError } from '@/services/errorReportingService';

/**
 * The NoteGo apps a feature can ship in, keyed by the canonical snake_case
 * names the admin Feature List grid writes to `feature_list/{key}.availability`
 * — never rename or alias them. `website` is the web app, `plugin` the Chrome
 * extension. The grid's `mobile_app` column isn't read here.
 */
export type FeatureApp = 'desktop_app' | 'website' | 'plugin';

export interface WebsiteFeature {
  key: string;
  label: string;
  beta: boolean;
  availability: Record<FeatureApp, boolean>;
}

const FEATURE_LIST_COLLECTION = 'feature_list';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * The features an admin flagged "Show on website", A→Z by label. Every field is
 * narrowed rather than cast: a doc without a usable label is dropped, and
 * anything but a literal `true` reads as not flagged / not available. A feature
 * in none of the three apps is dropped too — a row of ✗s would say it doesn't
 * exist anywhere.
 */
export const toWebsiteFeatures = (docs: { id: string; data: unknown }[]): WebsiteFeature[] =>
  docs
    .flatMap(({ id, data }) => {
      if (!isRecord(data) || data.show_on_website !== true) return [];
      const label = typeof data.label === 'string' ? data.label.trim() : '';
      if (!label) return [];
      const raw = isRecord(data.availability) ? data.availability : {};
      const availability = {
        desktop_app: raw.desktop_app === true,
        website: raw.website === true,
        plugin: raw.plugin === true,
      };
      if (!availability.desktop_app && !availability.website && !availability.plugin) return [];
      return [{ key: id, label, beta: data.beta === true, availability }];
    })
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

/**
 * Fetch the "Show on website" features from the public `feature_list` collection.
 */
export const fetchWebsiteFeatures = async (): Promise<WebsiteFeature[] | null> => {
  try {
    const snapshot = await getDocs(collection(db, FEATURE_LIST_COLLECTION));
    return toWebsiteFeatures(snapshot.docs.map((entry) => ({ id: entry.id, data: entry.data() })));
  } catch (error) {
    if (isFirebaseOfflineError(error)) {
      errorReporter.expected('Failed to fetch feature list (client offline)', error, {
        feature: 'firebase',
        operation: 'fetchWebsiteFeatures',
      });
    } else {
      errorReporter.critical('Failed to fetch feature list from Firebase', error, {
        feature: 'settings',
        operation: 'fetchWebsiteFeatures',
      });
    }
    return null;
  }
};
